import React, { useState, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
} from "@material-ui/core";
import {
  Chat as ChatIcon,
  SwapHoriz as TransferIcon,
  PersonOutline as AssigneeIcon,
  FlagOutlined as PriorityIcon,
  LabelOutlined as TagIcon,
  ArrowForward as MoveIcon,
  History as HistoryIcon,
  Wifi as ConnectionIcon,
} from "@material-ui/icons";
import { useHistory } from "react-router-dom";

const useStyles = makeStyles((theme) => ({
  menuPaper: {
    minWidth: 220,
    borderRadius: 10,
    boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
  },
  sectionLabel: {
    fontSize: "0.68rem",
    fontWeight: 600,
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    padding: theme.spacing(0.5, 2),
  },
  icon: {
    minWidth: 32,
    "& svg": { fontSize: 18 },
  },
}));

const PRIORITIES = [
  { value: "urgent", label: "Urgente", icon: "\u{1F534}" },
  { value: "high", label: "Alta", icon: "\u{1F7E0}" },
  { value: "normal", label: "Normal", icon: "⚪" },
  { value: "low", label: "Baixa", icon: "\u{1F535}" },
];

/**
 * QuickActionsMenu — menu contextual para ações rápidas no card.
 *
 * Props:
 *  - ticket: objeto do ticket
 *  - anchorEl: elemento DOM para ancorar o menu
 *  - onClose: fechar menu
 *  - onChangePriority: (ticketId, priority) => void
 *  - onChangeAssignee: (ticketId) => void — abre seletor
 *  - onChangeQueue: (ticketId) => void — abre seletor
 *  - onChangeConnection: (ticketId) => void — abre seletor
 *  - onViewHistory: (ticketId) => void
 *  - isAdmin: boolean
 */
const QuickActionsMenu = ({
  ticket,
  anchorEl,
  onClose,
  onChangePriority,
  onChangeAssignee,
  onChangeQueue,
  onChangeConnection,
  onViewHistory,
  isAdmin = false,
}) => {
  const classes = useStyles();
  const history = useHistory();
  const [prioritySubmenu, setPrioritySubmenu] = useState(null);

  const handleOpenTicket = useCallback(() => {
    if (ticket?.uuid) {
      history.push(`/tickets/${ticket.uuid}`);
    }
    onClose();
  }, [ticket?.uuid, history, onClose]);

  const handlePriority = useCallback(
    (priority) => {
      if (onChangePriority) onChangePriority(ticket.id, priority);
      setPrioritySubmenu(null);
      onClose();
    },
    [ticket?.id, onChangePriority, onClose]
  );

  if (!ticket || !anchorEl) return null;

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={onClose}
        classes={{ paper: classes.menuPaper }}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {/* Ações principais */}
        <MenuItem onClick={handleOpenTicket}>
          <ListItemIcon className={classes.icon}>
            <ChatIcon />
          </ListItemIcon>
          <ListItemText primary="Abrir conversa" />
        </MenuItem>

        <Divider />
        <Typography className={classes.sectionLabel}>Ações rápidas</Typography>

        {/* Trocar vendedor */}
        <MenuItem
          onClick={() => {
            onChangeAssignee && onChangeAssignee(ticket.id);
            onClose();
          }}
        >
          <ListItemIcon className={classes.icon}>
            <AssigneeIcon />
          </ListItemIcon>
          <ListItemText primary="Trocar vendedor" />
        </MenuItem>

        {/* Trocar fila */}
        <MenuItem
          onClick={() => {
            onChangeQueue && onChangeQueue(ticket.id);
            onClose();
          }}
        >
          <ListItemIcon className={classes.icon}>
            <TransferIcon />
          </ListItemIcon>
          <ListItemText primary="Trocar fila" />
        </MenuItem>

        {/* Trocar conexão */}
        {isAdmin && (
          <MenuItem
            onClick={() => {
              onChangeConnection && onChangeConnection(ticket.id);
              onClose();
            }}
          >
            <ListItemIcon className={classes.icon}>
              <ConnectionIcon />
            </ListItemIcon>
            <ListItemText primary="Trocar conexão" />
          </MenuItem>
        )}

        {/* Alterar prioridade */}
        <MenuItem
          onClick={(e) => setPrioritySubmenu(e.currentTarget)}
        >
          <ListItemIcon className={classes.icon}>
            <PriorityIcon />
          </ListItemIcon>
          <ListItemText primary="Alterar prioridade" />
        </MenuItem>

        <Divider />

        {/* Ver histórico */}
        <MenuItem
          onClick={() => {
            onViewHistory && onViewHistory(ticket.id);
            onClose();
          }}
        >
          <ListItemIcon className={classes.icon}>
            <HistoryIcon />
          </ListItemIcon>
          <ListItemText primary="Ver histórico" />
        </MenuItem>
      </Menu>

      {/* Priority submenu */}
      <Menu
        anchorEl={prioritySubmenu}
        open={Boolean(prioritySubmenu)}
        onClose={() => setPrioritySubmenu(null)}
        classes={{ paper: classes.menuPaper }}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        {PRIORITIES.map((p) => (
          <MenuItem
            key={p.value}
            onClick={() => handlePriority(p.value)}
            selected={ticket.priority === p.value}
          >
            <ListItemIcon className={classes.icon}>
              <span>{p.icon}</span>
            </ListItemIcon>
            <ListItemText primary={p.label} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default React.memo(QuickActionsMenu);
